/**
 * Currency catalog — the deployment-level currency selection (Client Setup →
 * Currencies). A fork picks WHICH currencies its users can display money in
 * (any number); the CurrencyToggle renders from this set, and lib/utils'
 * display-currency context converts through the catalog's rates.
 *
 * Selection is build-time via `VITE_CURRENCIES` (comma-separated ISO codes,
 * e.g. `VITE_CURRENCIES=USD,EUR,GBP`). Unset → the historical default pair
 * (USD + AUD), so the shared demo is unchanged by construction. A company's
 * NATIVE currency (demoCompanies.currency) is always displayable even if a
 * fork's list omits it — the toggle unions it in.
 *
 * Rates are demo-grade static FX (per 1 USD). A customer fork wanting live
 * rates swaps `fxPerUsd` for a feed at the same seam; every one of the ~119
 * formatCurrency call sites is already agnostic.
 */

export interface CurrencyDef {
  /** ISO 4217 code — written verbatim in VITE_CURRENCIES. */
  code: string
  /** Short toggle label, e.g. "US$", "€". */
  label: string
  /** Full display name for the Client Setup tick-list. */
  name: string
  /** Demo-grade static rate: units of this currency per 1 USD. */
  fxPerUsd: number
}

export const CURRENCY_CATALOG: CurrencyDef[] = [
  { code: 'USD', label: 'US$', name: 'US Dollar', fxPerUsd: 1 },
  { code: 'AUD', label: 'A$', name: 'Australian Dollar', fxPerUsd: 1.55 },
  { code: 'EUR', label: '€', name: 'Euro', fxPerUsd: 0.92 },
  { code: 'GBP', label: '£', name: 'British Pound', fxPerUsd: 0.79 },
  { code: 'CAD', label: 'C$', name: 'Canadian Dollar', fxPerUsd: 1.37 },
  { code: 'JPY', label: '¥', name: 'Japanese Yen', fxPerUsd: 155 },
  { code: 'CHF', label: 'CHF', name: 'Swiss Franc', fxPerUsd: 0.88 },
  { code: 'NZD', label: 'NZ$', name: 'New Zealand Dollar', fxPerUsd: 1.68 },
  { code: 'SGD', label: 'S$', name: 'Singapore Dollar', fxPerUsd: 1.33 },
  { code: 'INR', label: '₹', name: 'Indian Rupee', fxPerUsd: 84 },
  { code: 'AED', label: 'AED', name: 'UAE Dirham', fxPerUsd: 3.67 },
  { code: 'MXN', label: 'MX$', name: 'Mexican Peso', fxPerUsd: 18.5 },
]

const BY_CODE = new Map(CURRENCY_CATALOG.map((c) => [c.code, c]))

export function currencyDef(code: string): CurrencyDef | undefined {
  return BY_CODE.get(code)
}

/** The historical built-in pair — what an unset VITE_CURRENCIES resolves to,
 *  so existing builds (incl. the shared demo) are unchanged by construction. */
export const DEFAULT_CURRENCY_CODES = ['USD', 'AUD'] as const

/** Deployment-enabled display currencies, catalog order. Unknown codes in the
 *  env are ignored (fail-safe); an empty/unset env yields the default pair. */
export function enabledCurrencyCodes(): string[] {
  const raw = (import.meta.env as Record<string, string | undefined>).VITE_CURRENCIES?.trim()
  if (!raw) return [...DEFAULT_CURRENCY_CODES]
  const wanted = new Set(
    raw.split(',').map((t) => t.trim().toUpperCase()).filter(Boolean),
  )
  const resolved = CURRENCY_CATALOG.filter((c) => wanted.has(c.code)).map((c) => c.code)
  return resolved.length > 0 ? resolved : [...DEFAULT_CURRENCY_CODES]
}
